import { UserAddOutlined, UserOutlined } from "@ant-design/icons";
import { RouteComponentProps, useParams } from "@reach/router";
import {
  Typography,
  Table,
  Col,
  Row,
  Button,
  Modal,
  notification,
  Switch,
  Space,
  Popconfirm,
} from "antd";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import EmployeeDetail from "../component/employee/detail";
import InvitationDetail from "../component/invitation/detail";
import InvitationStatus from "../component/invitation/status";
import { useStore, Employee, Invitation } from "../store";

const styles = {
  right: {
    float: "right" as const,
  },
};

interface Props extends RouteComponentProps {}
const UserList = (props: Props) => {
  const [invitation, setInvitation] = useState<Employee>();
  const [details, setDetails] = useState<Employee>();
  const { t } = useTranslation();
  const { multivers } = useStore();
  const { administrationId } = useParams();
  const administration = useMemo(
    () => multivers.administration(administrationId),
    [administrationId]
  );

  const invite = (employee: Employee) => async () => {
    try {
      await employee.invite();
    } catch (error) {
      const message = (error as Error)?.message;
      notification.error({
        message: t("Invitation failed"),
        description: message,
      });
    }
  };
  const revoke = (invitation?: Invitation) => async () => {
    try {
      await invitation?.delete();
      hideDetails();
      console.log("Revoked");
    } catch (error) {
      const message = (error as Error)?.message;
      notification.error({
        message: t("Revoke user failed"),
        description: message,
      });
    }
  };

  const remove = (employee: Employee) => async () => {
    try {
      await administration?.removeWorkOrders(employee);
      notification.success({
        message: t("Removed user work orders successfully"),
      });
    } catch (error) {
      const message = (error as Error)?.message;
      notification.error({
        message: t("Remove user work orders failed"),
        description: message,
      });
    }
  };

  const showInvitation = (employee: Employee) => () => {
    setInvitation(employee);
  };
  const hideInvitation = () => {
    setInvitation(undefined);
  };

  const showDetails = (employee: Employee) => () => {
    setDetails(employee);
  };
  const hideDetails = () => {
    setDetails(undefined);
  };

  const data = administration.employees.docs.slice();
  const isLoaded = data.reduce(
    (acc, employee) =>
      acc &&
      employee.isLoaded &&
      (!employee.invitation || employee.invitation.isLoaded),
    true
  );

  const columns = [
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
      sorter: (a: Employee, b: Employee) => a.name.localeCompare(b.name),
    },
    {
      title: t("Status"),
      dataIndex: "inivitationStatus",
      key: "inivitationStatus",
      sorter: (a: Employee, b: Employee) =>
        a.inivitationStatus.localeCompare(b.inivitationStatus),
      render: (_: any, employee: Employee) => (
        <InvitationStatus invitation={employee.invitation} />
      ),
    },
    {
      title: t("Clients"),
      dataIndex: "clients",
      key: "clients",
      render: (_: any, employee: Employee) => (
        <Switch
          checkedChildren={t("All")}
          unCheckedChildren={t("Selection")}
          defaultChecked={employee.allClients}
          onChange={(checked, event) => {
            event.stopPropagation();
            employee.allClients = checked;
          }}
        />
      ),
    },
    {
      title: t("Actions"),
      dataIndex: "actions",
      key: "actions",
      width: 300,
      render: (_: any, employee: Employee) =>
        employee.invitation ? (
          <Space style={styles.right}>
            <Button icon={<UserOutlined />} onClick={showInvitation(employee)}>
              {t("Invitation")}
            </Button>
          </Space>
        ) : (
          <Space style={styles.right}>
            <Popconfirm
              title={t(
                "This operation will remove all work orders of this use and cannot be reversed. Are you sure you want to delete the workorders of this user?"
              )}
              okText={t("Remove")}
              okButtonProps={{ danger: true }}
              onConfirm={remove(employee)}
              cancelText={t("Cancel")}
            >
              <Button danger onClick={revoke(employee.invitation)}>
                {t("Remove workorders")}
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={invite(employee)}
            >
              {t("Invite")}
            </Button>
          </Space>
        ),
    },
  ];

  const rowHandler = (employee: Employee) => ({
    onClick: employee.invitation && showDetails(employee),
  });

  return (
    <>
      <Row>
        <Col flex="auto">
          <Typography.Title>{t("Users")}</Typography.Title>
        </Col>
      </Row>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        pagination={false}
        onRow={rowHandler}
        loading={!isLoaded}
      />
      <Modal
        title={t("User invitation")}
        visible={!!invitation}
        onCancel={hideInvitation}
        footer={[
          <Button key="ok" type="primary" onClick={hideInvitation}>
            {t("Ok")}
          </Button>,
        ]}
      >
        {invitation && (
          <InvitationDetail employee={invitation} onCancel={hideInvitation} />
        )}
      </Modal>
      <Modal
        title={t("User details")}
        visible={!!details}
        onCancel={hideDetails}
        footer={false}
      >
        {details && (
          <EmployeeDetail
            employee={details}
            onCancel={hideDetails}
            onSave={hideDetails}
            onRevoke={revoke(details?.invitation)}
          />
        )}
      </Modal>
    </>
  );
};

export default observer(UserList);
